<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import { Button } from '@lib/components';
  import { surveyInitializer } from '@lib/services/SurveyInitializer';
  import { browser } from '$app/environment';

  export let policyData: any = null; // Policy data passed from server
  export let privacyLink: string;
  export let goodbyeUrl: string;

  const dispatch = createEventDispatcher<{ consent: void; disagree: { url: string } }>();

  let bannerText: any = null;
  let policyHash: string = '';
  let loading = true;
  let error = false;

  onMount(() => {
    if (browser) {
      try {
        if (!policyData) {
          throw new Error('No policy data provided');
        }

        if (!policyData.banner) {
          throw new Error('Policy missing banner section');
        }

        // Extract policy hash and banner text
        policyHash = policyData.policy?.hash || '';
        bannerText = policyData.banner;

        if (!bannerText.title || !bannerText.message) {
          throw new Error('Banner missing required fields (title, message)');
        }
      } catch (err) {
        error = true;
        console.error('Failed to load consent policy:', err);
      }
      loading = false;
    }
  });

  function generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function setCookie(name: string, value: string, days: number): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict;Secure`;
  }

  async function handleConsent() {
    if (!bannerText || !policyData) return;

    const uuid = generateUUID();
    const language = (navigator.language || 'en').toLowerCase();

    const consentData = {
      uuid: uuid,
      policyId: policyData.policy.id,
      policyHash: policyHash,
      timestamp: Date.now(),
      language: language,
      categories: policyData.banner?.settings?.trackingCategories || ['essential']
    };

    localStorage.setItem(`moui_consent_${uuid}`, JSON.stringify(consentData));
    setCookie('moui_consent_token', uuid, policyData.banner?.settings?.cookieRetentionDays || 365);

    surveyInitializer.grantConsent();

    // Refresh consent data in surveyController and send page_load tracking
    try {
      const { SurveyController } = await import('$lib/controllers/surveyController');
      SurveyController.refreshConsentData();
      // Send page_load tracking now that consent is granted
      await SurveyController.trackPageLoad();
    } catch (error) {
      console.warn('Failed to refresh consent data or track page load:', error);
    }

    dispatch('consent');
  }

  function handleDisagree() {
    if (!bannerText) return;

    dispatch('disagree', { url: goodbyeUrl });
  }
</script>

{#if !loading && bannerText && !error}
<div
  class="gdpr-consent-banner"
  transition:fade={{ delay: 300, duration: 300 }}
>
  <div class="banner-overlay"></div>
  <div class="banner-content">
    <div class="consent-text">
      <h3 class="consent-title">{bannerText.title}</h3>
      <p class="consent-message">{bannerText.message}</p>
      <p class="consent-contact">
        <a href={privacyLink} target="_blank" rel="noopener noreferrer" class="privacy-link">
          {bannerText.privacyLinkText}
        </a>
      </p>
    </div>
    <div class="consent-actions">
      <Button
        label={bannerText.agreeLabel}
        variant="primary"
        size="md"
        inline={true}
        on:click={handleConsent}
      />
      <Button
        label={bannerText.disagreeLabel}
        variant="info"
        inline={true}
        on:click={handleDisagree}
      />
    </div>
  </div>
</div>
{/if}

<style lang="postcss">
  .gdpr-consent-banner {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100dvh;
    z-index: 2000;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    pointer-events: auto;
    font-family: var(--theme-font-family, var(--font-family, system-ui, sans-serif));
  }

  .banner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.65);
  }

  .banner-content {
    position: relative;
    background-color: var(--theme-background-color, rgba(255, 255, 255, 0.98));
    color: var(--theme-text-color, #1f2937);
    padding: 1.5rem;
    box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-height: 50dvh;
    max-width: 800px;
    overflow-y: auto;
    align-items: flex-start;
  }

  .consent-actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .consent-text {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .consent-title {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0;
    color: var(--theme-primary-color);
  }

  .consent-message {
    font-size: 0.95rem;
    line-height: 1.5;
    margin: 0;
    font-weight: 500;
    color: var(--theme-text-color, inherit);
  }

  .consent-contact {
    font-size: 0.85rem;
    margin: 0;
    opacity: 0.8;
    font-weight: 400;
    color: var(--theme-text-color-secondary, var(--theme-text-color, inherit));
  }

  .privacy-link {
    color: var(--theme-primary-color);
    text-decoration: underline;
    font-weight: 500;
  }

  .privacy-link:hover {
    opacity: 0.8;
  }

  /* Button styling overrides for the banner */
  .banner-content :global(button) {
    align-self: flex-start;
  }

  @media (min-width: 768px) {
    .banner-content {
      margin: 2rem auto;
      border-radius: 3px;
    }
  }
</style>